/**
 * Venue connection state + global trading mode — the single source of truth for "where can I
 * actually place a real order?", used app-wide so the whole UI speaks one language.
 *
 * A venue resolves to one of three states from `account_partners` (connection first, then
 * balance, per the product model):
 *   - `live`    connected + credential ok + funded  → real orders work now      (🟢)
 *   - `nofunds` connected + credential ok but $0     → real would fail on balance (🟡)
 *   - `off`     not linked / inactive / pending      → paper only                (⚪)
 *
 * The global **trading mode** (paper ⇄ real) is the spine: paper opens every venue and books
 * the internal book; real focuses the app on the venues you can execute on.
 */
import { useMemo } from "react";
import { useAccountPartners } from "./queries.js";
import { fmtMoney } from "./format.js";

// The global trading mode lives in a leaf module (no data-layer imports) to avoid an import
// cycle; re-exported here so venue-aware UI can pull both from one place.
export { getTradingMode, setTradingMode, useTradingMode, tradingModeStore, type TradingMode } from "./trading.js";

export type VenueState = "live" | "nofunds" | "off";

export interface VenueInfo {
  partnerId: string;
  name: string;
  logoUrl?: string | null;
  state: VenueState;
  connected: boolean;
  funded: boolean;
  /** The balance to DISPLAY: `expected_balance` (live, accounts for open-order holds)
   *  when the API provides it, else the raw `balance`. See {@link rawBalance}. */
  balance: number | null;
  /** The last venue-read balance (~60s cadence); may lag `balance` when orders are open. */
  rawBalance: number | null;
  /** Amount currently held against open/pending orders, if the API reports it. */
  pending: number | null;
  /** True when the raw balance is behind the expected balance (orders in flight). */
  isStale: boolean;
  currency: string | null;
  /** The ATLAS internal book = your paper wallet, not a real connected venue. */
  isPaper: boolean;
}

/** Format a balance by its currency: ATLAS is play money (never a `$`); everything else is USD. */
export function fmtVenueBalance(balance: number | null, currency: string | null): string {
  if (balance == null) return "—";
  if ((currency ?? "").toUpperCase() === "ATLAS") return `${Math.round(balance).toLocaleString()} ATLAS`;
  return fmtMoney(balance);
}

function computeState(connected: boolean, funded: boolean): VenueState {
  if (!connected) return "off";
  return funded ? "live" : "nofunds";
}

export interface VenueStatus {
  byId: Map<string, VenueInfo>;
  venues: VenueInfo[];
  /** Real connected venues (excludes the ATLAS paper book). */
  realVenues: VenueInfo[];
  /** The ATLAS internal book = paper wallet, if present. */
  paperWallet: VenueInfo | undefined;
  get: (partnerId: string | null | undefined) => VenueInfo | undefined;
  /** State for a partner id — `off` when unknown, so unknown venues read as paper-only. */
  stateOf: (partnerId: string | null | undefined) => VenueState;
  /** Counts + balances over REAL venues only (the paper wallet is separate). */
  liveCount: number;
  connectedCount: number;
  realBalance: number;
  paperBalance: number;
  /** True while account_partners is still loading (avoid flashing "0 venues"). */
  loading: boolean;
}

export function useVenueStatus(): VenueStatus {
  const q = useAccountPartners();
  return useMemo(() => {
    const byId = new Map<string, VenueInfo>();
    for (const ap of q.data?.account_partners ?? []) {
      const connected = ap.status === "active" && ap.credential_status === "connected";
      const rawBalance = ap.balance ?? null;
      // Prefer the live, order-aware expected_balance for display; fall back to raw balance.
      const balance = ap.expected_balance ?? rawBalance;
      const funded = connected && (balance ?? 0) > 0;
      const isPaper = (ap.currency ?? "").toUpperCase() === "ATLAS";
      byId.set(ap.partner_id, {
        partnerId: ap.partner_id,
        name: ap.partner_name ?? ap.partner_id,
        logoUrl: ap.logo_url,
        state: computeState(connected, funded),
        connected,
        funded,
        balance,
        rawBalance,
        pending: ap.pending ?? null,
        isStale: ap.is_stale === true || (ap.expected_balance != null && ap.expected_balance !== rawBalance),
        currency: ap.currency ?? null,
        isPaper,
      });
    }
    const venues = [...byId.values()];
    const realVenues = venues.filter((v) => !v.isPaper);
    return {
      byId,
      venues,
      realVenues,
      paperWallet: venues.find((v) => v.isPaper),
      get: (id) => (id ? byId.get(id) : undefined),
      stateOf: (id) => (id ? byId.get(id)?.state ?? "off" : "off"),
      liveCount: realVenues.filter((v) => v.state === "live").length,
      connectedCount: realVenues.filter((v) => v.connected).length,
      realBalance: realVenues.reduce((n, v) => n + (v.balance ?? 0), 0),
      paperBalance: venues.filter((v) => v.isPaper).reduce((n, v) => n + (v.balance ?? 0), 0),
      loading: q.isLoading,
    };
  }, [q.data, q.isLoading]);
}
